import { LoadingPage } from 'components/LoadingPage'
import { AuthProvider, useAuthContext } from 'contexts/auth.context'
import { CreateHearingPage } from 'pages/CreateHearing'
import DownloadHearingPage from 'pages/DownloadHearing'
import DownloadTeenagerPage from 'pages/DownloadTeenager'
import SearchHearingPage from 'pages/SearchHearing'
import SearchTeenagerPage from 'pages/SearchTeenager'
import WelcomePage from 'pages/Welcome'
import React, { ReactElement, useEffect, useState } from 'react'
import { hasAuthParams, useAuth } from 'react-oidc-context'
import { Navigate, Route, Routes as Switch, useLocation } from 'react-router-dom'
import {
  Funcionalidades,
  hearingFunctionalities,
  teenagerRecordFunctionalities,
} from 'utils/helpers/funcionalidades'
import { TeenagersRecordPage } from 'pages/TeenagersRecord'
import PendingProcessHearing from 'pages/PendingProcessHearing'
import ViewPendingProcessHearing from 'pages/ViewPendingProcessHearing'
import { TeenagersEditPage } from 'pages/TeenagersEdit'
import { TeenagerRecordAuditPage } from 'pages/Report/TeenagerRecordAudit'
import { SearchNotificationsPage } from 'pages/SearchNotifications'
import { SearchCreasPage } from 'pages/SearchCreas'
import { CreateCreasPage } from 'pages/CreateCreas'
import { StepGeneralDataPage } from 'pages/CreateHearing/components/StepGeneralData'
import { StepIdentificationPage } from 'pages/CreateHearing/components/StepIdentification'
import { StepSummaryPage } from 'pages/CreateHearing/components/StepSummary'
import { StepNarrativesPage } from 'pages/CreateHearing/components/StepNarratives'
import { StepCircumstancesPage } from 'pages/CreateHearing/components/StepCircumstances'
import { SocioeducationMeasureEditPage } from 'pages/SocioeducationMeasureEdit'
import { SocioeducationMeasureIdentificationEdit } from 'pages/SocioeducationMeasureEdit/Identification'
import ViewSummaryHearing from 'pages/ViewSummaryHearing'
import { FamilyDataPage } from 'pages/SocioeducationMeasureEdit/FamilyData'
import { MeioAbertoAcompanhamentoPage } from 'pages/SocioeducationMeasureEdit/Accompaniment'
import MeasuresAppliedPage from 'pages/SocioeducationMeasureEdit/MeasuresApplied'
import { ProceduralDataPage } from 'pages/SocioeducationMeasureEdit/ProceduralData'

type ProtectedRouteProps = {
  children: ReactElement
  isAuthenticated: boolean
  permission: string
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  isAuthenticated,
  permission,
}) => {
  const location = useLocation()
  const { handleHasPermission, isLoading } = useAuthContext()

  const userHasRequiredHole = handleHasPermission(permission)

  if (isAuthenticated && !userHasRequiredHole && !isLoading) {
    return <Navigate to='/' state={{ from: location }} />
  }

  return children
}

export const Routes: React.FC = () => {
  const auth = useAuth()
  const { handleHasPermission } = useAuthContext()

  const [hasTriedSigning, setHasTriedSigning] = useState<boolean>(false)

  useEffect(() => {
    const signInIfNecessary = async () => {
      try {
        if (
          !hasAuthParams() &&
          !auth.isAuthenticated &&
          !auth.activeNavigator &&
          !auth.isLoading &&
          !hasTriedSigning
        ) {
          await auth.signinRedirect()
          setHasTriedSigning(true)
        }
      } catch (error) {
        console.error('Error during signinRedirect:', error)
      }
    }

    signInIfNecessary()
  }, [auth, hasTriedSigning])

  if (auth.isLoading) {
    return <LoadingPage />
  }

  if (auth.error) {
    return <div>Oops... {auth.error.message}</div>
  }

  return (
    <AuthProvider>
      <Switch>
        <Route path='/' element={<WelcomePage />} />
        <Route
          path='/consultar-oitiva'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={hearingFunctionalities.consultar}
            >
              <SearchHearingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/nova-oitiva/:id?'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={hearingFunctionalities.cadastrar}
            >
              <CreateHearingPage />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to='dados-gerais' replace />} />
          <Route path='dados-gerais' element={<StepGeneralDataPage />} />
          <Route path='identificacao' element={<StepIdentificationPage />} />
          <Route path='circunstancias' element={<StepCircumstancesPage />} />
          <Route path='narrativas' element={<StepNarrativesPage />} />
          <Route path='resumo' element={<StepSummaryPage />} />
        </Route>
        <Route
          path='/download-oitiva/:id?'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={hearingFunctionalities.download}
            >
              <DownloadHearingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/visualizar-resumo-oitiva/:id?'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.oitiva.visualizar}
            >
              <ViewSummaryHearing />
            </ProtectedRoute>
          }
        />
        <Route
          path='/consultar-adolescente'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={teenagerRecordFunctionalities.consultar}
            >
              <SearchTeenagerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/ficha-adolescente/:id?'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={teenagerRecordFunctionalities.visualizar}
            >
              <TeenagersRecordPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/ficha-adolescente/:id?/imprimir'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={teenagerRecordFunctionalities.imprimir}
            >
              <DownloadTeenagerPage
                isPrint={handleHasPermission(teenagerRecordFunctionalities.imprimir)}
                isExport={false}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path='/ficha-adolescente/:id?/exportar'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={teenagerRecordFunctionalities.exportar}
            >
              <DownloadTeenagerPage
                isPrint={false}
                isExport={handleHasPermission(teenagerRecordFunctionalities.exportar)}
              />
            </ProtectedRoute>
          }
        />
        <Route
          path='/ficha-adolescente/:id/editar'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={teenagerRecordFunctionalities.editar}
            >
              <TeenagersEditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path='/processos-pendentes'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.integraPolicial.consultar}
            >
              <PendingProcessHearing />
            </ProtectedRoute>
          }
        />
        <Route
          path='/processos-pendentes/:pendingProcessHearingId'
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.integraPolicial.visualizar}
            >
              <ViewPendingProcessHearing />
            </ProtectedRoute>
          }
        />
        <Route
          path={'/auditoria/ficha-adolescente'}
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.relatorio.auditoria.fichaAdolescente.consultar}
            >
              <TeenagerRecordAuditPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={'/notificacoes'}
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.notificacoes.consultar}
            >
              <SearchNotificationsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={'/creas'}
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.creas.consultar}
            >
              <SearchCreasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={'/novo-creas'}
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.creas.cadastrar}
            >
              <CreateCreasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={'/novo-creas/:id'}
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.creas.editar}
            >
              <CreateCreasPage />
            </ProtectedRoute>
          }
        />
        <Route
          path={'/meio-aberto/:id/'}
          element={
            <ProtectedRoute
              isAuthenticated={auth.isAuthenticated}
              permission={Funcionalidades.meioAberto.visualizar}
            >
              <SocioeducationMeasureEditPage />
            </ProtectedRoute>
          }
        >
          <Route path={'identificacao'} element={<SocioeducationMeasureIdentificationEdit />} />
          <Route path={'dados-familiares'} element={<FamilyDataPage />} />
          <Route path={'dados-processuais'} element={<ProceduralDataPage />} />
          <Route path={'medidas-aplicadas'} element={<MeasuresAppliedPage />} />
          <Route path={'acompanhamento'} element={<MeioAbertoAcompanhamentoPage />} />
        </Route>
      </Switch>
    </AuthProvider>
  )
}

export default Routes
